/**
 * DatasetDetailPage — items in a single dataset, joined with their label snapshots.
 *
 * Read-only: items are added through the public /v1 API, not the app. Items
 * without an attached label show a "—" placeholder rather than a broken
 * affordance.
 */

import { useState } from 'react'
import { ArrowLeft, Database, Pencil, Trash2 } from 'lucide-react'
import { Navigate, Link, useNavigate, useParams } from 'react-router-dom'
import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import { toast } from 'sonner'

import { useTeam } from '@/contexts/TeamContext'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useDataset,
  useDatasetItems,
  useDeleteDataset,
  useRemoveDatasetItem,
  type DatasetItem,
} from '@/hooks/useDatasets'
import { useLabels } from '@/hooks/useLabels'

interface RubricScores {
  quality?: number
  [key: string]: unknown
}

function summarizeRubric(scores: RubricScores | undefined): string | null {
  if (!scores || typeof scores.quality !== 'number') return null
  return `${scores.quality.toString()}/5`
}

interface ItemRowProps {
  item: DatasetItem
  datasetId: string
  approvedById: Map<string, boolean>
  rubricById: Map<string, RubricScores | undefined>
  onRemove: (traceId: string) => void
  removeBusy: boolean
}

function ItemRow({
  item,
  approvedById,
  rubricById,
  onRemove,
  removeBusy,
}: ItemRowProps): React.JSX.Element {
  const { isRestricted } = useTeam()
  const approved = item.label_id ? approvedById.get(item.label_id) : undefined
  const rubric = item.label_id ? rubricById.get(item.label_id) : undefined
  const score = summarizeRubric(rubric)

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        <span className="inline-flex items-center gap-1.5">
          {/* Not a link: the raw trace viewer is gone. The id + copy button
              are what people actually use here. */}
          <span className="text-foreground">{item.trace_id}</span>
          <CopyButton value={item.trace_id} label="Copy trace ID" />
        </span>
      </TableCell>
      <TableCell>
        {item.label_id ? (
          <div className="flex items-center gap-2">
            {approved !== undefined && (
              <Badge variant={approved ? 'default' : 'outline'}>
                {approved ? 'Approved' : 'Not approved'}
              </Badge>
            )}
            {score && (
              <span className="text-xs text-muted-foreground">Quality {score}</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
        {new Date(item.added_at).toLocaleString()}
      </TableCell>
      <TableCell className="text-right">
        {/* No item removal on restricted demo teams (TRA-832) */}
        {!isRestricted && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onRemove(item.trace_id)
            }}
            disabled={removeBusy}
            aria-label="Remove from dataset"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}

export default function DatasetDetailPage(): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>()
  const navigate = useNavigate()
  const { isRestricted } = useTeam()
  const { data: dataset, isLoading: datasetLoading, isError, error } = useDataset(datasetId)
  const { data: items, isLoading: itemsLoading } = useDatasetItems(datasetId)
  // Labels are fetched team-wide so we can join item.label_id → label data
  // without N+1 by-trace lookups. Pre-PMF label counts are small.
  const { data: labels } = useLabels()
  const deleteMutation = useDeleteDataset()
  const removeItemMutation = useRemoveDatasetItem()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const approvedById = new Map<string, boolean>()
  const rubricById = new Map<string, RubricScores | undefined>()
  for (const label of labels ?? []) {
    approvedById.set(label.id, label.approved)
    rubricById.set(label.id, label.rubric_scores as RubricScores)
  }

  // The page owns both mutations (see ConfirmDialog for why), so a refusal
  // lands in the dialog or a toast instead of an unhandled rejection.
  function handleDelete(): void {
    if (!datasetId) return
    setDeleteError(null)
    deleteMutation.mutate(
      { datasetId },
      {
        onSuccess: () => {
          setConfirmOpen(false)
          toast.success('Dataset deleted')
          navigate('/datasets')
        },
        onError: (error) => {
          setDeleteError(getApiErrorDetail(error) ?? 'The dataset was not deleted.')
        },
      },
    )
  }

  function handleRemoveItem(traceId: string): void {
    if (!datasetId) return
    removeItemMutation.mutate(
      { datasetId, traceId },
      {
        onSuccess: () => {
          toast.success('Removed from dataset')
        },
        onError: (error) => {
          toast.error(getApiErrorDetail(error) ?? 'The trace was not removed.')
        },
      },
    )
  }

  const isLoading = datasetLoading || itemsLoading

  return (
    <PageContainer className="space-y-6">
      <Button variant="ghost" size="sm" asChild className="-ml-2">
        <Link to="/datasets">
          <ArrowLeft className="mr-2 h-4 w-4" />
          All datasets
        </Link>
      </Button>

      {datasetLoading && <Skeleton className="h-32 w-full" />}

      {isError && getApiErrorStatus(error) === 404 && <Navigate to="/datasets" replace />}
      {isError && getApiErrorStatus(error) !== 404 && (
        <p className="text-sm text-destructive">Failed to load dataset.</p>
      )}

      {!datasetLoading && !isError && dataset && (
        <>
          <PageHeader
            title={dataset.display_name}
            description={
              dataset.description || `Slug: ${dataset.slug}`
            }
            actions={
              // No dataset edit/delete on restricted demo teams (TRA-832)
              isRestricted ? undefined : (
                <>
                  <Button asChild variant="outline">
                    <Link to={`/datasets/${dataset.id}/edit`}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDeleteError(null)
                      setConfirmOpen(true)
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4 text-destructive" />
                    Delete
                  </Button>
                </>
              )
            }
          />

          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 rounded-md border border-border bg-muted/30 p-4 md:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Slug
              </dt>
              <dd className="mt-1 font-mono text-sm">{dataset.slug}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Items
              </dt>
              <dd className="mt-1 text-sm tabular-nums">{dataset.item_count}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Created
              </dt>
              <dd className="mt-1 text-sm">
                {new Date(dataset.created_at).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Updated
              </dt>
              <dd className="mt-1 text-sm">
                {new Date(dataset.updated_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>

          {itemsLoading && (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}

          {!isLoading && items && items.length === 0 && (
            <EmptyState
              icon={Database}
              title="No traces yet"
              description="Datasets are filled through the /v1 API. POST the traces you want to keep."
            />
          )}

          {!isLoading && items && items.length > 0 && (
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trace ID</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="hidden md:table-cell">Added</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <ItemRow
                      key={`${item.dataset_id}:${item.trace_id}`}
                      item={item}
                      datasetId={dataset.id}
                      approvedById={approvedById}
                      rubricById={rubricById}
                      onRemove={handleRemoveItem}
                      removeBusy={removeItemMutation.isPending}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Delete dataset"
            description={
              <>
                This will permanently delete <strong>{dataset.display_name}</strong> and
                remove all its items. Labels on the underlying traces are not affected.
              </>
            }
            confirmLabel="Delete"
            pendingLabel="Deleting..."
            destructive
            isPending={deleteMutation.isPending}
            error={deleteError}
            onConfirm={handleDelete}
          />
        </>
      )}
    </PageContainer>
  )
}
